// 選択範囲の変形: ぼかし (分離ガウス)、拡張 / 縮小 (ユークリッド距離変換)、境界線。

import type { Rect } from '@/types/geometry'
import { emptyMask, maskAt, trimMask, type MaskData } from './mask'

function gaussianKernel(radius: number): Float32Array {
  const sigma = Math.max(0.01, radius / 2)
  const size = Math.ceil(radius) * 2 + 1
  const k = new Float32Array(size)
  const half = Math.ceil(radius)
  let sum = 0
  for (let i = 0; i < size; i++) {
    const x = i - half
    k[i] = Math.exp(-(x * x) / (2 * sigma * sigma))
    sum += k[i]!
  }
  for (let i = 0; i < size; i++) k[i]! /= sum
  return k
}

/** 境界をぼかす。radius px (0 なら変更なし) */
export function featherMask(m: MaskData, radius: number): MaskData | null {
  if (radius <= 0) return m
  const pad = Math.ceil(radius)
  const rect: Rect = { x: m.rect.x - pad, y: m.rect.y - pad, w: m.rect.w + pad * 2, h: m.rect.h + pad * 2 }
  const k = gaussianKernel(radius)
  const half = pad
  const { w, h } = rect
  const tmp = new Float32Array(w * h)
  // 横
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let s = 0
      for (let i = 0; i < k.length; i++) s += k[i]! * maskAt(m, rect.x + x + i - half, rect.y + y)
      tmp[y * w + x] = s
    }
  }
  // 縦
  const out = emptyMask(rect)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let s = 0
      for (let i = 0; i < k.length; i++) {
        const yy = y + i - half
        if (yy >= 0 && yy < h) s += k[i]! * tmp[yy * w + x]!
      }
      out.data[y * w + x] = Math.round(s)
    }
  }
  return trimMask(out)
}

/**
 * 1 次元の二乗ユークリッド距離変換 (Felzenszwalb & Huttenlocher)。
 * f: 入力 (0 = 前景、INF = 背景)、戻り値: 各位置から最も近い前景までの距離の二乗
 */
function edt1d(f: Float64Array, n: number, out: Float64Array, v: Int32Array, z: Float64Array): void {
  let k = 0
  v[0] = 0
  z[0] = -Infinity
  z[1] = Infinity
  for (let q = 1; q < n; q++) {
    let s = ((f[q]! + q * q) - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!)
    while (s <= z[k]!) {
      k--
      s = ((f[q]! + q * q) - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!)
    }
    k++
    v[k] = q
    z[k] = s
    z[k + 1] = Infinity
  }
  k = 0
  for (let q = 0; q < n; q++) {
    while (z[k + 1]! < q) k++
    out[q] = (q - v[k]!) * (q - v[k]!) + f[v[k]!]!
  }
}

/** 2 次元距離変換。inside[i] が true のピクセルを前景として、各ピクセルの前景までの距離 */
export function distanceTransform(inside: (i: number) => boolean, w: number, h: number): Float64Array {
  const INF = 1e20
  const f = new Float64Array(w * h)
  for (let i = 0; i < w * h; i++) f[i] = inside(i) ? 0 : INF
  const maxN = Math.max(w, h)
  const col = new Float64Array(maxN)
  const outCol = new Float64Array(maxN)
  const v = new Int32Array(maxN)
  const z = new Float64Array(maxN + 1)
  // 列方向
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) col[y] = f[y * w + x]!
    edt1d(col, h, outCol, v, z)
    for (let y = 0; y < h; y++) f[y * w + x] = outCol[y]!
  }
  // 行方向
  const row = new Float64Array(maxN)
  const outRow = new Float64Array(maxN)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) row[x] = f[y * w + x]!
    edt1d(row, w, outRow, v, z)
    for (let x = 0; x < w; x++) f[y * w + x] = outRow[x]!
  }
  return f
}

/** 拡張 (amount > 0) / 縮小 (amount < 0)。しきい値 128 で 2 値化してから距離で判定 */
export function expandMask(m: MaskData, amount: number): MaskData | null {
  if (amount === 0) return m
  // 拡張は距離ぶん、縮小は 1px だけ余白を取り、矩形の外の背景が距離変換に入るようにする
  const pad = amount > 0 ? Math.ceil(amount) + 1 : 1
  const rect: Rect = { x: m.rect.x - pad, y: m.rect.y - pad, w: m.rect.w + pad * 2, h: m.rect.h + pad * 2 }
  const { w, h } = rect
  const get = (i: number) => maskAt(m, rect.x + (i % w), rect.y + Math.floor(i / w)) >= 128
  const out = emptyMask(rect)
  if (amount > 0) {
    const d = distanceTransform(get, w, h)
    for (let i = 0; i < w * h; i++) {
      const dist = Math.sqrt(d[i]!)
      // 境界で 1px の AA
      out.data[i] = dist <= amount ? 255 : dist < amount + 1 ? Math.round(255 * (amount + 1 - dist)) : 0
    }
  } else {
    // 縮小: 背景までの距離が |amount| より大きければ残す (余白 1px は maskAt が 0 を返すので背景になる)
    const d = distanceTransform(i => !get(i), w, h)
    const a = -amount
    for (let i = 0; i < w * h; i++) {
      const dist = Math.sqrt(d[i]!)
      out.data[i] = get(i) ? (dist > a + 1 ? 255 : dist > a ? Math.round(255 * (dist - a)) : 0) : 0
    }
  }
  return trimMask(out)
}

/** 境界線: 幅 width の帯 (境界の両側に width/2) */
export function borderMask(m: MaskData, width: number): MaskData | null {
  const half = width / 2
  const outer = expandMask(m, half)
  const inner = expandMask(m, -half)
  if (!outer) return null
  const out = emptyMask(outer.rect)
  for (let y = 0; y < outer.rect.h; y++) {
    for (let x = 0; x < outer.rect.w; x++) {
      const gx = outer.rect.x + x, gy = outer.rect.y + y
      out.data[y * outer.rect.w + x] = Math.max(0, maskAt(outer, gx, gy) - (inner ? maskAt(inner, gx, gy) : 0))
    }
  }
  return trimMask(out)
}

export interface RefineOptions {
  /** 滑らかに 0..100 (半径 px 相当) */
  smooth: number
  /** ぼかし px */
  feather: number
  /** コントラスト 0..100 (%) */
  contrast: number
  /** エッジをシフト -100..100 (%) → 拡張 / 縮小 px は smooth+feather の半径に対する割合 */
  shift: number
}

/** 「選択範囲を調整」: 滑らかに → ぼかし → コントラスト → エッジのシフト */
export function refineMask(m: MaskData, o: RefineOptions): MaskData | null {
  let cur: MaskData | null = m
  if (o.smooth > 0 && cur) {
    // ぼかしてから中間値で二値化すると小さな凹凸が均される
    const f = featherMask(cur, o.smooth)
    if (f) {
      for (let i = 0; i < f.data.length; i++) f.data[i] = f.data[i]! >= 128 ? 255 : 0
      cur = trimMask(f)
    }
  }
  if (o.feather > 0 && cur) cur = featherMask(cur, o.feather)
  if (o.contrast > 0 && cur) {
    const k = 1 + (o.contrast / 100) * 9
    for (let i = 0; i < cur.data.length; i++) cur.data[i] = Math.max(0, Math.min(255, Math.round(128 + (cur.data[i]! - 128) * k)))
    cur = trimMask(cur)
  }
  if (o.shift !== 0 && cur) {
    const radius = Math.max(1, o.smooth + o.feather)
    const px = (o.shift / 100) * radius
    cur = expandMask(cur, px)
  }
  return cur
}
